import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/api/adminOneClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — Watchtowers™ (Phase 26G).
 *
 * Seven domain watchtowers in a single AdminOne command surface. Each tower
 * uses the existing Phase 25 intelligence routes (NiSi-scored deterministic
 * data) and the Phase 26 startups/grants routes. Mock-data fallbacks render
 * if any endpoint fails. No autonomous execution; HITL required.
 */

const DOMAINS = [
  { id: "markets",     label: "Markets",     icon: "📈", color: "#10b981", method: "intelligenceTopOpps" },
  { id: "crypto",      label: "Crypto",      icon: "₿",  color: "#fcd34d", method: "intelligenceCrypto" },
  { id: "fx",          label: "FX",          icon: "💱", color: "#67e8f9", method: "intelligenceForex" },
  { id: "sports",      label: "Sports",      icon: "🏆", color: "#a78bfa", method: "intelligenceSports" },
  { id: "healthcare",  label: "Healthcare",  icon: "✚",  color: "#fda4af", method: "intelligenceHealthcare" },
  { id: "startups",    label: "Startups",    icon: "🚀", color: "#c5a059", method: "startupsPublic" },
  { id: "africa",      label: "Africa",      icon: "🌍", color: "#f59e0b", method: "intelligenceAfrica" },
];

const DIR_COLOR = { bullish: "#10b981", bearish: "#ef4444", neutral: "#c5a059" };

function ScoreBar({ value, palette }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const grads = {
    opp:  "linear-gradient(90deg, #ffd966, #c5a059)",
    conf: "linear-gradient(90deg, #34d399, #10b981)",
    risk: "linear-gradient(90deg, #fda4af, #ef4444)",
  };
  return (
    <div style={S.bar}>
      <div style={{ ...S.barFill, width: `${v}%`, background: grads[palette] || grads.opp }} />
    </div>
  );
}

function DirPill({ d }) {
  const dir = (d || "neutral").toLowerCase();
  const c = DIR_COLOR[dir] || DIR_COLOR.neutral;
  return (
    <span style={{ ...S.pill, color: c, borderColor: c, background: `${c}1a` }}>
      {dir.toUpperCase()}
    </span>
  );
}

function SignalCard({ item, testid }) {
  const sym = item.symbol || item.name || "—";
  return (
    <div style={S.card} data-testid={testid}>
      <div style={S.cardHead}>
        <code style={S.sym}>{sym}</code>
        <DirPill d={item.direction} />
      </div>
      {item.asset_class && (
        <div style={S.metaRow}>
          <span style={S.metaLbl}>ASSET</span>
          <code style={S.metaVal}>{item.asset_class}</code>
        </div>
      )}
      {item.sector && (
        <div style={S.metaRow}>
          <span style={S.metaLbl}>SECTOR</span>
          <code style={S.metaVal}>{item.sector}</code>
        </div>
      )}
      {item.region && (
        <div style={S.metaRow}>
          <span style={S.metaLbl}>REGION</span>
          <code style={S.metaVal}>{item.region}</code>
        </div>
      )}
      {item.horizon && (
        <div style={S.metaRow}>
          <span style={S.metaLbl}>HORIZON</span>
          <code style={S.metaVal}>{item.horizon}</code>
        </div>
      )}
      <div style={S.scoreRow}>
        <div style={S.scoreCell}>
          <span style={S.scoreLbl}>OPP</span>
          <span style={{ ...S.scoreVal, color: "#c5a059" }}>{item.opportunity_score ?? "—"}%</span>
          <ScoreBar value={item.opportunity_score} palette="opp" />
        </div>
        <div style={S.scoreCell}>
          <span style={S.scoreLbl}>CONF</span>
          <span style={{ ...S.scoreVal, color: "#10b981" }}>{item.confidence_score ?? "—"}%</span>
          <ScoreBar value={item.confidence_score} palette="conf" />
        </div>
        <div style={S.scoreCell}>
          <span style={S.scoreLbl}>RISK</span>
          <span style={{ ...S.scoreVal, color: "#fca5a5" }}>{item.risk_score ?? "—"}%</span>
          <ScoreBar value={item.risk_score} palette="risk" />
        </div>
      </div>
      {typeof item.pct_change_24h === "number" && (
        <div style={S.metaRow}>
          <span style={S.metaLbl}>24H</span>
          <code style={{ ...S.metaVal, color: item.pct_change_24h >= 0 ? "#10b981" : "#ef4444" }}>
            {item.pct_change_24h >= 0 ? "+" : ""}{item.pct_change_24h}%
          </code>
        </div>
      )}
      <ComplianceLock variant="inline" short testid={`${testid}-compliance`} />
    </div>
  );
}

const MOCK_FALLBACK = {
  markets: [
    { symbol: "SPY",   asset_class: "etfs",   direction: "bullish", opportunity_score: 64, confidence_score: 70, risk_score: 35, pct_change_24h: 0.42 },
    { symbol: "QQQ",   asset_class: "etfs",   direction: "neutral", opportunity_score: 55, confidence_score: 60, risk_score: 40, pct_change_24h: 0.10 },
  ],
  crypto: [
    { symbol: "BTC",   asset_class: "crypto", direction: "bullish", opportunity_score: 72, confidence_score: 65, risk_score: 55, pct_change_24h: 1.85 },
    { symbol: "ETH",   asset_class: "crypto", direction: "bullish", opportunity_score: 68, confidence_score: 60, risk_score: 60, pct_change_24h: 1.20 },
  ],
  fx: [
    { symbol: "EUR/USD", asset_class: "forex", direction: "bullish", opportunity_score: 60, confidence_score: 58, risk_score: 45, pct_change_24h: 0.18 },
    { symbol: "GBP/USD", asset_class: "forex", direction: "bearish", opportunity_score: 55, confidence_score: 55, risk_score: 50, pct_change_24h: -0.12 },
  ],
  sports: [
    { symbol: "NBA — DEMO", asset_class: "sports", direction: "neutral", opportunity_score: 50, confidence_score: 48, risk_score: 60 },
  ],
  healthcare: [
    { symbol: "XLV", asset_class: "healthcare", direction: "neutral", opportunity_score: 55, confidence_score: 60, risk_score: 40, pct_change_24h: 0.05 },
  ],
  startups: [
    { name: "BiomeChain", sector: "Biotech / Microbiome", region: "USA", direction: "bullish", opportunity_score: 80, confidence_score: 78, risk_score: 50 },
  ],
  africa: [
    { symbol: "NGX:DANGCEM", asset_class: "african", region: "Nigeria", direction: "neutral", opportunity_score: 58, confidence_score: 55, risk_score: 50 },
  ],
};

export default function OracleWatchtowers() {
  const [domain, setDomain] = useState("markets");
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const out = {};
      await Promise.all(
        DOMAINS.map(async (d) => {
          try {
            const fn = adminApi[d.method];
            const { data: r } = await fn();
            // Normalize to array of items.
            let items = [];
            if (Array.isArray(r)) items = r;
            else if (Array.isArray(r?.items)) items = r.items;
            else if (Array.isArray(r?.opportunities)) items = r.opportunities;
            else if (Array.isArray(r?.signals)) items = r.signals;
            else if (Array.isArray(r?.cryptos)) items = r.cryptos;
            else if (Array.isArray(r?.pairs)) items = r.pairs;
            else if (Array.isArray(r?.events)) items = r.events;
            else if (Array.isArray(r?.markets)) items = r.markets;
            else if (Array.isArray(r?.matches)) items = r.matches;
            else if (Array.isArray(r?.sectors)) items = r.sectors;
            if (!items.length && MOCK_FALLBACK[d.id]) items = MOCK_FALLBACK[d.id];
            out[d.id] = items.slice(0, 24);
          } catch {
            out[d.id] = MOCK_FALLBACK[d.id] || [];
          }
        })
      );
      setData(out);
    } catch (e) {
      setErr(e?.message || "load_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => Object.fromEntries(
    DOMAINS.map((d) => [d.id, (data[d.id] || []).length])
  ), [data]);

  const active = DOMAINS.find((d) => d.id === domain);
  const items = data[domain] || [];

  return (
    <div style={S.canvas} data-testid="oracle-watchtowers-page">
      <ComplianceLock variant="banner" testid="oracle-watchtowers-compliance-top" />
      <header style={S.header}>
        <div>
          <h1 style={S.title}>ORACLE Watchtowers™</h1>
          <p style={S.subtitle}>
            Seven domain watchtowers · NiSi-scored deterministic outputs · LOGIX-2 daily synthesis.
            Research / education only — no autonomous trading or wagering.
          </p>
        </div>
        <div style={S.headerActions}>
          <span style={S.livePill}>LIVE · 7 TOWERS</span>
          <button type="button" onClick={load} disabled={busy} style={S.refresh} data-testid="watchtowers-refresh">
            {busy ? "…" : "↻ Refresh"}
          </button>
        </div>
      </header>

      {err && <div style={S.err}>⚠ {err}</div>}

      <nav style={S.towersRow} role="tablist" aria-label="ORACLE Watchtower domains">
        {DOMAINS.map((d) => {
          const isActive = domain === d.id;
          return (
            <button
              key={d.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setDomain(d.id)}
              data-testid={`watchtower-tab-${d.id}`}
              style={{
                ...S.tower,
                borderColor: isActive ? d.color : "rgba(255,255,255,0.08)",
                background: isActive ? `${d.color}1a` : "rgba(11,15,20,0.94)",
              }}
            >
              <span style={{ ...S.towerIcon, color: d.color }}>{d.icon}</span>
              <span style={S.towerLbl}>{d.label}</span>
              <span style={S.towerCount}>{totals[d.id] ?? "—"}</span>
            </button>
          );
        })}
      </nav>

      <section style={S.panel} data-testid={`watchtower-panel-${domain}`}>
        <h2 style={S.heading}>
          <span style={{ color: active?.color }}>{active?.icon}</span> {active?.label} Watchtower
          <span style={S.countChip}>{items.length} signals</span>
        </h2>
        {!items.length ? (
          <p style={S.empty}>No signals available for {active?.label}. Showing fallback if any.</p>
        ) : (
          <div style={S.grid}>
            {items.map((it, i) => (
              <SignalCard key={`${domain}-${i}`} item={it} testid={`watchtower-signal-${domain}-${i}`} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const S = {
  canvas: { minHeight: "100vh", background: "radial-gradient(circle at top left, rgba(16,185,129,0.10), #040608 30%, #020304 100%)", color: "#f8fafc", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { padding: "20px 26px 16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  title: { color: "#c5a059", fontSize: 24, margin: 0, fontWeight: 800 },
  subtitle: { color: "#94a3b8", fontSize: 13, maxWidth: 820, marginTop: 6 },
  headerActions: { display: "flex", gap: 8, alignItems: "center" },
  livePill: { color: "#10b981", border: "1px solid rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.10)", padding: "6px 11px", borderRadius: 999, fontSize: 10.5, fontWeight: 900, fontFamily: "monospace", letterSpacing: "0.10em" },
  refresh: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.10em" },
  err: { margin: "10px 26px 0", padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12.5 },
  towersRow: { display: "flex", gap: 10, padding: "16px 26px 0", flexWrap: "wrap" },
  tower: { display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", border: "1px solid", borderRadius: 12, cursor: "pointer", color: "#e2e8f0", fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: "0.04em", whiteSpace: "nowrap" },
  towerIcon: { fontSize: 18 },
  towerLbl: { fontWeight: 800 },
  towerCount: { background: "rgba(197,160,89,0.18)", color: "#ffd966", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 800 },
  panel: { margin: "16px 26px 30px", background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.14)", borderRadius: 16, padding: 22 },
  heading: { color: "#e2e8f0", marginTop: 0, display: "flex", alignItems: "center", gap: 10, fontSize: 16, flexWrap: "wrap" },
  countChip: { fontSize: 11, fontFamily: "'Space Mono', monospace", letterSpacing: "0.08em", background: "rgba(197,160,89,0.10)", color: "#c5a059", border: "1px solid rgba(197,160,89,0.28)", padding: "3px 9px", borderRadius: 999 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 10, marginTop: 12 },
  card: { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 6 },
  cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  sym: { color: "#e2e8f0", fontSize: 13.5, fontFamily: "'Space Mono', monospace" },
  pill: { fontSize: 10, fontFamily: "'Space Mono', monospace", letterSpacing: "0.10em", fontWeight: 800, padding: "3px 8px", border: "1px solid", borderRadius: 999 },
  scoreRow: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 6 },
  scoreCell: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 8, padding: "6px 8px", display: "flex", flexDirection: "column", gap: 3 },
  scoreLbl: { fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.10em", color: "#64748b" },
  scoreVal: { fontSize: 14, fontWeight: 800 },
  bar: { height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 4, transition: "width 280ms ease" },
  metaRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 4 },
  metaLbl: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em" },
  metaVal: { background: "rgba(255,255,255,0.04)", color: "#cbd5e1", padding: "2px 6px", borderRadius: 6, fontFamily: "'Space Mono', monospace", fontSize: 11 },
  empty: { gridColumn: "1 / -1", color: "#94a3b8", textAlign: "center", padding: 28, border: "1px dashed rgba(255,255,255,0.10)", borderRadius: 12, fontFamily: "'Space Mono', monospace", fontSize: 12.5 },
};
